import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Copy, Check, Ban, RotateCcw, KeyRound, Loader2, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { sourceLabel, formatTime } from "@/lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [label, setLabel] = useState("");
  const [source, setSource] = useState("web");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [tgBusy, setTgBusy] = useState(false);
  const [tgOk, setTgOk] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/keys");
    setKeys(data.keys);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    try {
      await api.post("/admin/keys", { label: label.trim(), source });
      setLabel("");
      toast.success("Ключ створено");
      load();
    } catch {
      toast.error("Не вдалося створити ключ");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    await api.post(`/admin/keys/${id}/revoke`);
    toast.success("Ключ відкликано");
    load();
  };
  const activate = async (id) => {
    await api.post(`/admin/keys/${id}/activate`);
    toast.success("Ключ активовано");
    load();
  };
  const copy = (key) => {
    navigator.clipboard.writeText(key);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const connectTelegram = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/set-webhook", { base_url: BACKEND_URL });
      if (data.result?.ok) {
        setTgOk(true);
        toast.success("Telegram-бот підключено");
      } else {
        toast.error("Помилка Telegram: " + JSON.stringify(data.result?.description || data.result));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не вдалося підключити Telegram");
    } finally {
      setTgBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 border-b border-slate-200 bg-white flex items-center px-4 md:px-8 gap-3 sticky top-0 z-10">
        <Link to="/admin" data-testid="back-to-inbox-btn"
          className="w-9 h-9 rounded-[10px] hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors">
          <ArrowLeft size={18} />
        </Link>
        <div className="w-9 h-9 rounded-[10px] bg-[#0F76BC] flex items-center justify-center text-white">
          <KeyRound size={18} />
        </div>
        <h1 className="font-head font-extrabold tracking-tight">Управління API-ключами</h1>
      </header>

      <div className="max-w-4xl mx-auto p-4 md:p-10 space-y-8">
        <div className="bg-white border border-slate-200 rounded-[12px] p-6 shadow-sm">
          <h2 className="font-head font-bold text-lg mb-4">Створити новий ключ</h2>
          <form onSubmit={create} className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="key-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Назва (напр. Сайт ipplus.ua)"
              className="flex-1 border border-slate-200 rounded-[12px] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC] transition-[border-color,box-shadow]"
            />
            <select
              data-testid="key-source-select"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="border border-slate-200 rounded-[12px] px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]"
            >
              <option value="web">Сайт</option>
              <option value="app">Мобільний додаток</option>
              <option value="telegram">Телеграм</option>
              <option value="viber">Viber</option>
            </select>
            <button
              data-testid="create-key-btn"
              type="submit"
              disabled={creating}
              className="bg-[#0F76BC] hover:bg-[#0c5f96] text-white px-6 py-3 rounded-[12px] font-semibold transition-[background-color,transform] active:scale-95 shadow-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {creating ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
              Створити
            </button>
          </form>
        </div>

        <div className="bg-white border border-slate-200 rounded-[12px] p-6 shadow-sm">
          <h2 className="font-head font-bold text-lg mb-1 flex items-center gap-2">
            <MessageCircle size={18} className="text-[#0F76BC]" /> Telegram-бот
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Підключіть webhook, щоб звернення з Telegram-бота падали в цей інбокс (канал «Телеграм»).
            Користувачі пишуть вашому боту — повідомлення зʼявляються тут.
          </p>
          <button
            data-testid="connect-telegram-btn"
            onClick={connectTelegram}
            disabled={tgBusy}
            className="bg-[#0F76BC] hover:bg-[#0c5f96] text-white px-6 py-2.5 rounded-[12px] font-semibold transition-[background-color,transform] active:scale-95 shadow-sm flex items-center gap-2 disabled:opacity-60"
          >
            {tgBusy ? <Loader2 size={18} className="animate-spin" /> : <MessageCircle size={18} />}
            Підключити / оновити webhook
          </button>
          {tgOk && <p className="text-sm text-emerald-600 mt-3 font-medium">✓ Webhook активний</p>}
        </div>

        <div className="space-y-3" data-testid="keys-list">
          {keys.map((k) => (
            <div key={k.id} data-testid={`key-row-${k.id}`}
              className={`bg-white border rounded-[12px] p-4 shadow-sm flex flex-col md:flex-row md:items-center gap-3 ${k.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-slate-800">{k.label}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[#0F76BC]/10 text-[#0F76BC]">
                    {sourceLabel(k.source)}
                  </span>
                  {k.active ? (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-600">Активний</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-500">Відкликаний</span>
                  )}
                </div>
                <button onClick={() => copy(k.key)} data-testid={`copy-key-${k.id}`}
                  className="group flex items-center gap-2 text-sm font-mono text-slate-500 hover:text-[#0F76BC] transition-colors">
                  <span className="truncate max-w-[260px] md:max-w-md">{k.key}</span>
                  {copied === k.key ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} className="opacity-50 group-hover:opacity-100" />}
                </button>
                <div className="text-[11px] text-slate-400 mt-1">Створено {formatTime(k.created_at)}</div>
              </div>
              {k.active ? (
                <button onClick={() => revoke(k.id)} data-testid={`revoke-key-${k.id}`}
                  className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-[12px] text-red-500 border border-red-200 hover:bg-red-50 font-medium text-sm transition-colors">
                  <Ban size={15} /> Відкликати
                </button>
              ) : (
                <button onClick={() => activate(k.id)} data-testid={`activate-key-${k.id}`}
                  className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-[12px] text-emerald-600 border border-emerald-200 hover:bg-emerald-50 font-medium text-sm transition-colors">
                  <RotateCcw size={15} /> Активувати
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
